import React, { useCallback, useEffect, useState } from "react";

import { completeModule, getCourseDetails, startModule } from "services";

import { recordPageView, scrollPage, useCancelAsyncOnUnmount, useParams, useTranslation, useUserContext } from "utils";

import { CourseOverlay, ErrorBoundary, Skeleton, SocialSharePopover, SubPage } from "components";

import { CourseComplete } from "pages/app/courses/components/CourseComplete";
import { CourseModule } from "pages/app/courses/components/CourseModule";
import { CourseModuleFooter } from "pages/app/courses/components/CourseModuleFooter";
import { CourseDetails } from "pages/app/courses/components/Details";
import { CourseHead } from "pages/app/courses/components/Head";
import { CourseInfo } from "pages/app/courses/components/Info";
import { CoursePlan } from "pages/app/courses/components/Plan";

export const Course = () => {
  const { t } = useTranslation("courses");
  const { user } = useUserContext();
  const { slug } = useParams();
  const { link } = useParams();

  const [activeModule, setActiveModule] = useState(null);
  const [activeChapter, setActiveChapter] = useState(null);
  const [showOverlay, setShowOverlay] = useState(false);
  const [showCompletionOverlay, setShowCompletionOverlay] = useState(false);
  const [loading, setLoading] = useState(true);
  const [moduleCompletionLoading, setModuleCompletionLoading] = useState(false);
  const [course, setCourse] = useState(null);
  const courseStarted = course && course.progress.summary >= 0;
  const signal = useCancelAsyncOnUnmount()();

  const [share, setShare] = useState({
    title: t("meta:course.title"),
    summary: t("meta:course.description"),
    link: `${process.env.REACT_APP_FARM_URL}/course/${link}`,
    image: `${process.env.REACT_APP_FARM_URL}/images/misc/share.png`,
  });

  const findModule = useCallback(
    (moduleId) => {
      if (course) {
        const chapter = course.chapters.find((chapter) => chapter.modules.find((module) => module.id === moduleId));

        const module = chapter.modules.find((module) => module.id === moduleId);
        return { chapter, module };
      }
    },
    [course]
  );

  const handleChangeModule = useCallback(
    (moduleId) => {
      const { chapter, module } = findModule(moduleId);

      startModule({
        moduleId: module.id,
        signal,
      }).then(() => {
        setActiveChapter(chapter);
        setActiveModule(module);
        scrollPage(document.getElementsByClassName("ant-drawer-body")[0]);
      });
    },
    [findModule, signal]
  );

  const loadCourseDetails = useCallback(() => {
    setModuleCompletionLoading(true);

    getCourseDetails({
      slug,
      signal,
    })
      .then((res) => {
        setCourse(res.course);

        setShare({
          title: res.course.title,
          link: `${process.env.REACT_APP_FARM_URL}/course/${res.course.slug}`,
          summary: res.course.teaser,
          image: `${process.env.REACT_APP_FARM_URL}/images/misc/share.png`,
        });
      })
      .finally(() => {
        setModuleCompletionLoading(false);
        setLoading(false);
      });
  }, [slug, signal]);

  const handleCompleteModule = useCallback(() => {
    setModuleCompletionLoading(true);

    completeModule({
      chapterId: activeChapter.id,
      courseSlug: slug,
      moduleId: activeModule.id,
      signal,
    })
      .then((res) => {
        if (res.courseComplete) {
          setShowOverlay(false);
          setShowCompletionOverlay(true);
        }
      })
      .finally(() => {
        loadCourseDetails();
      });
  }, [activeChapter, loadCourseDetails, slug, activeModule, signal]);

  const handleSelectModule = useCallback(
    ({ chapter, module }) => {
      startModule({
        moduleId: module.id,
        signal,
      })
        .then(() => {
          setActiveChapter(chapter);
          setActiveModule(module);
        })
        .finally(() => {
          setShowOverlay(true);
        });
    },
    [signal]
  );

  useEffect(() => {
    loadCourseDetails(true);
    recordPageView("course", slug);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <SubPage
      loading={loading}
      pageName={"course-overview"}
      crumbs={[{ name: t("navigation.knowledge"), link: "/search/knowledge" }, { name: t("navigation.course") }]}
      crumbSide={
        !loading &&
        course && (
          <span className="header-group">
            <span className="share">
              <SocialSharePopover t={t} saved={false} share={share} />
            </span>
          </span>
        )
      }
    >
      {loading ? (
        <section className="container content">
          <Skeleton type="paragraph" rows={2} />
          <Skeleton type="paragraph" rows={10} />
        </section>
      ) : (
        course && (
          <>
            <ErrorBoundary>
              <CourseHead
                course={course}
                courseStarted={courseStarted}
                setActiveChapter={setActiveChapter}
                setActiveModule={setActiveModule}
                setCourse={setCourse}
                setShowOverlay={setShowOverlay}
                slug={slug}
                t={t}
              />
            </ErrorBoundary>
            {!courseStarted && (
              <ErrorBoundary>
                <CourseInfo course={course} t={t} />
              </ErrorBoundary>
            )}
            {courseStarted && (
              <section className="container content mini padded-top">
                <ErrorBoundary>
                  <CoursePlan
                    course={course}
                    courseStarted={courseStarted}
                    completedModules={course.progress.completedModules}
                    onModuleSelect={(v) => handleSelectModule(v)}
                  />
                </ErrorBoundary>
              </section>
            )}

            <ErrorBoundary>
              <CourseDetails course={course} courseStarted={courseStarted} handleSelectModule={handleSelectModule} t={t} />
            </ErrorBoundary>
            {activeModule && (
              <CourseOverlay
                className="course-overlay"
                footer={
                  <ErrorBoundary>
                    <CourseModuleFooter
                      activeModule={activeModule}
                      complete={course.progress.completedModules.includes(activeModule.id)}
                      moduleCompletionLoading={moduleCompletionLoading}
                      onComplete={(v) => handleCompleteModule(v)}
                      onChangeModule={(v) => handleChangeModule(v)}
                    />
                  </ErrorBoundary>
                }
                title={t("courseOverview.lesson")}
                visible={showOverlay}
                onClose={() => setShowOverlay(false)}
              >
                <ErrorBoundary>
                  <CourseModule chapter={activeChapter} module={activeModule} />
                </ErrorBoundary>
              </CourseOverlay>
            )}
            {showCompletionOverlay && (
              <CourseOverlay visible={showCompletionOverlay} onClose={() => setShowCompletionOverlay(false)}>
                <ErrorBoundary>
                  <CourseComplete course={course} referralToken={user.referralToken} onClose={() => setShowCompletionOverlay(false)} />
                </ErrorBoundary>
              </CourseOverlay>
            )}
          </>
        )
      )}
    </SubPage>
  );
};
